import { CustomerType, DiscountType, PaymentStatus } from '@repo/common';
import { z } from 'zod';

import { PaginationResponseSchema } from '@/shared/global.schema';

export const StatsSchema = z.object({
    data: z.object({
        total_customers: z.number(),
        total_products: z.number(),
        total_suppliers: z.number(),
        total_orders: z.number(),
        total_expense: z.number(),
        total_income: z.number(),
        total_low_stock_products: z.number(),
        total_expired_products: z.number(),
    }),
    message: z.string(),
    statusCode: z.number(),
});

export type StatsType = z.infer<typeof StatsSchema>;

const MonthDataSchema = z.object({
    month_name: z.string(),
    total_orders: z.string().transform((val) => parseInt(val, 10)),
    complete_orders: z.string().transform((val) => parseInt(val, 10)),
    due_orders: z.string().transform((val) => parseInt(val, 10)),
});

export const SalesOverviewSchema = z.object({
    data: z.array(MonthDataSchema),
    message: z.string(),
    statusCode: z.number(),
});

export type SalesOverview = z.infer<typeof SalesOverviewSchema>;

export const OrderStatsSchema = z.object({
    data: z.object({
        total_orders: z.number(),
        total_wholesale_orders: z.number(),
        total_retail_orders: z.number(),
        total_paid: z.number(),
        total_due: z.number(),
    }),
    message: z.string(),
    statusCode: z.number(),
});

export const SalesReportSchema = z.object({
    data: z.array(
        z.object({
            user_name: z.string().optional(),
            customer_name: z.string().nullable().optional(),
            total_due_amount: z.number().optional(),
            created_at: z.string().optional(),
            discount_value: z.number().optional(),
            discount_type: z.nativeEnum(DiscountType).nullable().optional(),
            invoice_number: z.string().optional(),
            note: z.string().nullable().optional(),
            status: z.nativeEnum(PaymentStatus).optional(),
            total_price: z.number().optional(),
            type: z.string().optional(),
        }),
    ),
    message: z.string(),
    statusCode: z.number(),
});

export const SupplierReportItemSchema = z.any();

export type SupplierReportItem = z.infer<typeof SupplierReportItemSchema>;

export const SupplierReportResponseSchema = z.object({
    data: z.array(SupplierReportItemSchema),
    pagination: PaginationResponseSchema,
    message: z.string(),
    statusCode: z.number(),
});

export const InvoiceReportSchema = z.object({
    data: z.array(
        z.object({
            invoice_number: z.string(),
            customer_name: z.string(),
            mobile: z.string(),
            total_amount: z.string(),
            paid_amount: z.string(),
            due_amount: z.string(),
            payment_status: z.string(),
            payment_method: z.string(),
        }),
    ),
    message: z.string(),
    statusCode: z.number(),
});

export const CustomerReportItemSchema = z.object({
    total_due: z.number(),
    total_paid: z.number(),

    name: z.string(),
    email: z.string(),
    phone_number: z.string(),
    address: z.string(),
    customer_type: z.nativeEnum(CustomerType),
    created_at: z.string(),
    updated_at: z.string(),
    created_by: z.string(),
    last_order_date: z.string().nullable(),
    total_orders: z.number(),
    total_price: z.number(),
});

export type CustomerReportItem = z.infer<typeof CustomerReportItemSchema>;

export const CustomersReportResponseSchema = z.object({
    data: z.array(CustomerReportItemSchema),
    pagination: PaginationResponseSchema,
    message: z.string(),
    statusCode: z.number(),
});

export type CustomerReportResponse = z.infer<
    typeof CustomersReportResponseSchema
>;

export const TopSellingProductSchema = z.object({
    product_name: z.string(),
    total_sales: z.coerce.number(),
    brand: z.string().nullable(),
    total_stock: z.coerce.number(),
});

export const TopSellingProductsSchema = z.object({
    data: z.array(TopSellingProductSchema),
    pagination: PaginationResponseSchema,
});

export type TopSellingProduct = z.infer<typeof TopSellingProductSchema>;

export type OrderStats = z.infer<typeof OrderStatsSchema>;
export type SalesReport = z.infer<typeof SalesReportSchema>;
export type SupplierReport = z.infer<typeof SupplierReportResponseSchema>;
export type InvoiceReport = z.infer<typeof InvoiceReportSchema>;

export const ExpenseReportItemSchema = z.any();

export type ExpenseReportItem = z.infer<typeof ExpenseReportItemSchema>;

export const ExpenseReportResponseSchema = z.object({
    data: z.array(ExpenseReportItemSchema),
    pagination: PaginationResponseSchema,
    message: z.string(),
    statusCode: z.number(),
});

export const SalesSummarySchema = z.object({
    data: z
        .object({
            total_orders: z.number().default(0).nullable(),
            total_estimated_profit_margin: z.number().default(0).nullable(),
            total_price: z.number().default(0).nullable(),
            total_paid_amount: z.number().default(0).nullable(),
            total_due_amount: z.number().default(0).nullable(),
        })
        .nullable(),
    message: z.string(),
    statusCode: z.number(),
});

export type TSalesSummary = z.infer<typeof SalesSummarySchema>;

export const PurchaseSummarySchema = z.object({
    data: z
        .object({
            total_purchases: z.number().default(0).nullable(),
            // total_estimated_profit_margin: z.number().default(0).nullable(),
            total_price: z.number().default(0).nullable(),
            total_paid_amount: z.number().default(0).nullable(),
            total_due_amount: z.number().default(0).nullable(),
        })
        .nullable(),
    message: z.string(),
    statusCode: z.number(),
});
